import Head from "next/head";
import { useRouter } from "next/router";

import { useCallback, useEffect, useState } from "react";

import Grid from "@mui/material/Grid";
import Zoom from "@mui/material/Zoom";

import config from "@config";
import useAccount from "@hooks/useAccount";
import useNodeContractVersionAbi from "@hooks/useNodeContractVersionAbi";
import type { MyLocale } from "@i18n";
import { useAppDispatch } from "@store/hooks";
import { getStore } from "@store/index";
import { setNodeManagerVersion } from "@store/slices/contract";
import { updateDevice } from "@store/slices/device";
import { setReferral } from "@store/slices/invite";
import { getFeature } from "@vaporfi/feature-flags";
import Cookies from "js-cookie";
import type { GetServerSideProps } from "next";
import { type I18nProps, useI18n } from "next-rosetta";
import { getSelectorsByUserAgent } from "react-device-detect";

import ExistingUser from "@components/ExistingUser";
import CreateNode from "@components/Modals/CreateNode";
import NewUser from "@components/NewUser";
import { isAddress } from "viem";

const NodesPage = ({ enableNodeCreation }) => {
	const i18n = useI18n<MyLocale>();
	const { t } = i18n;
	const dispatch = useAppDispatch();
	const router = useRouter();
	const { invite } = router.query;
	const [open, setOpen] = useState(false);
	const { account, active } = useAccount();
	const { nodesCountNew, nodesCountOld } = useNodeContractVersionAbi(account);
	const isNewUser = nodesCountOld === 0 && nodesCountNew === 0;

	const openDialog = useCallback(() => {
		if (active && account) {
			setOpen(true);
			return;
		}
	}, [active, account]);

	useEffect(() => {
		const referral = (invite as string)?.trim?.();

		if (account && referral && isAddress(referral)) {
			if (referral === account || referral === Cookies.get("referral")) {
				return;
			}

			Cookies.remove("referral");
			const now = Date.now();
			const expires = new Date(now + 1000 * 60 * 60 * 24 * 365);
			Cookies.set("referral", referral, { expires });
			dispatch(setReferral(referral));
			openDialog();
		}
	}, [account, invite, dispatch, openDialog]);

	useEffect(() => {
		if (nodesCountNew > 0 || isNewUser) {
			dispatch(setNodeManagerVersion(5));
		} else {
			// This is needed to make sure we show the migration to the user
			dispatch(setNodeManagerVersion(4));
		}
	}, [dispatch, nodesCountOld, nodesCountNew, isNewUser]);

	return (
		<>
			<Head>
				<title>
					{config.title} - {t("nodes.title")}
				</title>
				<meta
					httpEquiv="content-language"
					// content={contentLanguageMap[i18n.activeLocale]}
				/>
			</Head>

			<Zoom in>
				<Grid container>
					<Grid item xs={12}>
						{isNewUser ? (
							<NewUser
								onClick={openDialog}
								enableNodeCreation={enableNodeCreation}
							/>
						) : (
							<ExistingUser enableNodeCreation={enableNodeCreation} />
						)}
						<CreateNode open={open} setOpen={setOpen} />
					</Grid>
				</Grid>
			</Zoom>
		</>
	);
};

export default NodesPage;

// Server-side code
export const getServerSideProps: GetServerSideProps<I18nProps<MyLocale>> =
	getStore((store) => async (ctx) => {
		const locale = ctx.locale || ctx.defaultLocale;
		const { table = {} } = await import(`../i18n/${locale}`);
		store.dispatch(
			updateDevice(getSelectorsByUserAgent(ctx.req.headers["user-agent"])),
		);

		const enableNodeCreation = await getFeature("enableNodeCreation");

		return {
			props: { enableNodeCreation: Boolean(enableNodeCreation), table },
		};
	});
